import { useState, useEffect } from 'react';
import { X, Mail, LogOut, LayoutDashboard, Edit2 } from 'lucide-react';
import { Avatar } from './ui/Avatar';
import { RateLimitIndicator } from './ui/RateLimitIndicator';
import { User } from 'firebase/auth';

interface UserProfile {
  displayName: string;
  photoUrl?: string | null;
}

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  userProfile: UserProfile | null;
  onSaveProfile: (displayName: string) => Promise<void>;
  onGoogleSignIn: () => Promise<void>;
  onEmailSignIn?: (email: string, password: string) => Promise<void>;
  onEmailRegister?: (email: string, password: string, displayName: string) => Promise<void>;
  onPasswordReset?: (email: string) => Promise<void>;
  onSignOut: () => Promise<void>;
  isAdmin: boolean;
  adminRole: 'root' | 'admin' | null;
  showNotify: (message: string, type?: 'success' | 'error' | 'info') => void;
  onOpenAdminPanel?: () => void;
  allowAnonymousSignIn?: boolean; // Controls whether anonymous sign-in is allowed (shows warning when disabled)
}

export const ProfileModal = ({
  isOpen,
  onClose,
  user,
  userProfile,
  onSaveProfile,
  onGoogleSignIn,
  onEmailSignIn,
  onEmailRegister,
  onPasswordReset,
  onSignOut,
  isAdmin,
  adminRole: _adminRole, // Reserved for future use
  showNotify,
  onOpenAdminPanel,
  allowAnonymousSignIn = true, // Default to enabled for backward compatibility
}: ProfileModalProps) => {
  const [name, setName] = useState(userProfile?.displayName || '');
  const [loading, setLoading] = useState(false);
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);
  const [emailSignInLoading, setEmailSignInLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  // Default to email-signin view for anonymous users for better UX
  const getInitialView = () => {
    if (!user || user.isAnonymous) {
      return 'email-signin';
    }
    return 'menu';
  };
  
  const [view, setView] = useState<'menu' | 'edit' | 'email-signin' | 'email-register' | 'forgot-password'>(getInitialView());
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  
  // Check if user is signed in with Google (not anonymous)
  const isGoogleUser = user && user.providerData.some(provider => provider.providerId === 'google.com');
  
  // Check if user is authenticated (not anonymous)
  const isAuthenticated = user && !user.isAnonymous;

  // Reset view when opened
  useEffect(() => {
    if (isOpen) {
      setName(userProfile?.displayName || '');
      // Set appropriate default view based on user state
      if (!user || user.isAnonymous) {
        setView('email-signin'); // Default to login for anonymous users
      } else {
        setView('menu'); // Show profile menu for authenticated users
      }
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setDisplayName('');
    }
  }, [isOpen, userProfile, user]);

  // Focus management and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    // Prevent body scroll when modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Handle Escape key to close modal
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await onSaveProfile(name);
      setView('menu');
      showNotify('Profile updated!', 'success');
    } catch (err) {
      console.error(err);
      showNotify('Failed to update profile.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    if (!onEmailSignIn) {
      showNotify('Email sign-in is not available right now.', 'error');
      return;
    }
    setEmailSignInLoading(true);
    try {
      await onEmailSignIn(email, password);
      onClose();
    } catch (err) {
      console.error(err);
      // Error handling is done in onEmailSignIn
    } finally {
      setEmailSignInLoading(false);
    }
  };

  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim() || !displayName.trim()) return;
    
    // Validate password match
    if (password !== confirmPassword) {
      showNotify('Passwords do not match', 'error');
      return;
    }
    
    // Validate password strength
    if (password.length < 6) {
      showNotify('Password must be at least 6 characters long', 'error');
      return;
    }
    
    if (!onEmailRegister) {
      showNotify('Email registration is not available right now.', 'error');
      return;
    }

    setRegisterLoading(true);
    try {
      await onEmailRegister(email, password, displayName);
      onClose();
    } catch (err) {
      console.error(err);
      // Error handling is done in onEmailRegister
    } finally {
      setRegisterLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    if (!onPasswordReset) {
      showNotify('Password reset is not available right now.', 'error');
      return;
    }
    
    setResetPasswordLoading(true);
    try {
      await onPasswordReset(email);
      setView('email-signin'); // Return to sign-in view after sending reset email
    } catch (err) {
      console.error(err);
      // Error handling is done in onPasswordReset
    } finally {
      setResetPasswordLoading(false);
    }
  };


  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-modal-title"
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
          <h2 
            id="profile-modal-title"
            className="font-bold text-gray-800 text-lg"
          >
            {view === 'edit' 
              ? 'Edit Profile' 
              : view === 'email-signin' 
              ? 'Sign In' 
              : view === 'email-register'
              ? 'Create Account'
              : view === 'forgot-password'
              ? 'Reset Password'
              : 'Account'}
          </h2>
          <button
            onClick={onClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClose();
              }
            }}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded p-1 transition-colors"
            aria-label="Close account modal"
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        <div className="p-6">
          {view === 'menu' && (
            <div className="space-y-4">
              <div className="flex flex-col items-center mb-6">
                <Avatar
                  name={userProfile?.displayName || user?.displayName || 'Guest'}
                  size="xl"
                  className="mb-3"
                  url={user?.photoURL || userProfile?.photoUrl || undefined}
                />
                <h3 className="text-xl font-bold">
                  {userProfile?.displayName || user?.displayName || 'Guest User'}
                </h3>
                {(user?.email || userProfile?.photoUrl) && (
                  <p className="text-gray-600 text-sm">
                    {user?.email}
                  </p>
                )}
                <p className="text-gray-500 text-sm">
                  {!user || user.isAnonymous 
                    ? 'Anonymous' 
                    : isGoogleUser 
                    ? 'Signed in with Google' 
                    : user.email 
                    ? 'Signed in with Email' 
                    : 'Verified Reporter'}
                </p>
                {isAdmin && (
                  <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded mt-2 font-bold">
                    ADMIN ACCESS
                  </span>
                )}
              </div>

              {/* Only show auth options for anonymous users */}
              {!isAuthenticated && (
                <>
                  {/* Google sign-in is always available for anonymous users */}
                  <RateLimitIndicator onRateLimitChange={setIsRateLimited} />
                  {!allowAnonymousSignIn && user?.isAnonymous && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
                      <p className="text-xs text-yellow-800">
                        Google sign-in is now required to use this app. Please sign in to continue.
                      </p>
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      setGoogleSignInLoading(true);
                      try {
                        await onGoogleSignIn();
                        onClose();
                      } catch (error) {
                        // Error handling is done in onGoogleSignIn
                      } finally {
                        setGoogleSignInLoading(false);
                      }
                    }}
                    disabled={googleSignInLoading || isRateLimited}
                    className="w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {googleSignInLoading ? (
                      'Signing in...'
                    ) : isRateLimited ? (
                      'Rate limit active'
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="currentColor"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="currentColor"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Sign in with Google
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setView('email-register')}
                    className="w-full bg-blue-50 text-blue-700 font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-blue-100 transition-colors"
                  >
                    <Mail size={18} />
                    Register with Email
                  </button>
                </>
              )}

              {/* Show profile actions for authenticated users */}
              {isAuthenticated && (
                <>
                  <button
                    onClick={() => setView('edit')}
                    className="w-full bg-blue-50 text-blue-700 font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-blue-100 transition-colors"
                  >
                    <Edit2 size={18} />
                    Edit Profile
                  </button>

                  <button
                    onClick={async () => {
                      await onSignOut();
                      onClose();
                    }}
                    className="w-full bg-red-50 text-red-600 font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-red-100 transition-colors"
                  >
                    <LogOut size={18} />
                    Sign Out
                  </button>

                  <hr />
                </>
              )}

              {/* Admin Panel Access - Only shown if user is admin */}
              {isAdmin && onOpenAdminPanel && (
                <button
                  onClick={() => {
                    onOpenAdminPanel();
                    onClose();
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:from-blue-700 hover:to-blue-800 transition-all shadow-md"
                >
                  <LayoutDashboard size={18} />
                  Open Admin Panel
                </button>
              )}
            </div>
          )}

          {view === 'edit' && (
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Display Name
                </label>
                <input
                  autoFocus
                  required
                  type="text"
                  placeholder="e.g. Juan Dela Cruz"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2"
              >
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
              <button
                type="button"
                onClick={() => setView('menu')}
                className="w-full text-gray-400 text-sm hover:text-gray-600"
              >
                Cancel
              </button>
            </form>
          )}

          {view === 'email-signin' && (
            <div className="space-y-4">
              <form onSubmit={handleEmailSignIn} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Email Address
                  </label>
                  <input
                    autoFocus
                    required
                    type="email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                    <span>Password</span>
                    <button
                      type="button"
                      onClick={() => setView('forgot-password')}
                      className="text-blue-600 hover:text-blue-700 text-xs font-normal normal-case"
                    >
                      Forgot password?
                    </button>
                  </label>
                  <input
                    required
                    type="password"
                    placeholder="Enter your password"
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  disabled={emailSignInLoading || isRateLimited}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emailSignInLoading ? 'Signing in...' : 'Sign In with Email'}
                </button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              <RateLimitIndicator onRateLimitChange={setIsRateLimited} />
              <button
                onClick={async () => {
                  setGoogleSignInLoading(true);
                  try {
                    await onGoogleSignIn();
                    onClose();
                  } catch (error) {
                    // Error handling is done in onGoogleSignIn
                  } finally {
                    setGoogleSignInLoading(false);
                  }
                }}
                disabled={googleSignInLoading || isRateLimited}
                className="w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {googleSignInLoading ? (
                  'Signing in...'
                ) : isRateLimited ? (
                  'Rate limit active'
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign in with Google
                  </>
                )}
              </button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setView('email-register')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Don&rsquo;t have an account? <span className="font-bold">Register here</span>
                </button>
              </div>
            </div>
          )}

          {view === 'email-register' && (
            <form onSubmit={handleEmailRegister} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Display Name
                </label>
                <input
                  autoFocus
                  required
                  type="text"
                  placeholder="e.g. Juan Dela Cruz"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Email Address
                </label>
                <input
                  required
                  type="email"
                  placeholder="your@email.com"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Password
                </label>
                <input
                  required
                  type="password"
                  placeholder="At least 6 characters"
                  minLength={6}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Confirm Password
                </label>
                <input
                  required
                  type="password"
                  placeholder="Re-enter your password"
                  minLength={6}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={registerLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {registerLoading ? 'Creating account...' : 'Create Account'}
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setView('email-signin')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Already have an account? Sign in
                </button>
              </div>
              <button
                type="button"
                onClick={() => setView('menu')}
                className="w-full text-gray-400 text-sm hover:text-gray-600"
              >
                Cancel
              </button>
            </form>
          )}

          {view === 'forgot-password' && (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <p className="text-sm text-gray-600">
                Enter your email address and we&rsquo;ll send you a link to reset your password.
              </p>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Email Address
                </label>
                <input
                  autoFocus
                  required
                  type="email"
                  placeholder="your@email.com"
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={resetPasswordLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetPasswordLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setView('email-signin')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Back to sign in
                </button>
              </div>
              <button
                type="button"
                onClick={() => setView('menu')}
                className="w-full text-gray-400 text-sm hover:text-gray-600"
              >
                Cancel
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
